import { useCallback, useContext } from 'react';

import { TrackingContext } from './TrackingContext';
import { TrackingEvents, TrackingSources, UI_TO_ANALYTICS_NAME_MAP } from './constants';
import type { TrackingAnalyticsData, TrackingData, TrackingEvent } from './types';

export type TrackFn = (event: TrackingEvent, payload: TrackingData) => void;

export function useTrack(): TrackFn {
  const config = useContext(TrackingContext);

  return useCallback(
    (event: TrackingEvent, payload: TrackingData) => {
      const { source, enabled } = config;

      if (source === TrackingSources.Mini) {
        // TODO: Verify whether separate mini-widget tracking and KSP guidelines should be implemented here. PMAR-1462
        return;
      }

      if (source === TrackingSources.Prematch && enabled) {
        const { tab, filter, elementOrder, element } = payload;

        let analyticsData: TrackingAnalyticsData = {
          name: 'prematch_stats',
          content_placement: 'main',
          page_type: 'sports_event_details_page',
        };

        switch (event) {
          case TrackingEvents.MainTabClick: {
            const analyticsTab = UI_TO_ANALYTICS_NAME_MAP[tab] ?? tab;
            analyticsData = {
              ...analyticsData,
              interaction_name: 'tab_clicked',
              element_name: analyticsTab,
              element_order: elementOrder,
              tab_info_name: analyticsTab,
              tab_info_level: '1',
            };
            break;
          }

          case TrackingEvents.FilterClick: {
            if (filter === undefined) {
              return;
            }
            const analyticsFilter = UI_TO_ANALYTICS_NAME_MAP[filter] ?? filter;
            analyticsData = {
              ...analyticsData,
              interaction_name: 'tab_clicked',
              element_name: analyticsFilter,
              element_order: elementOrder,
              tab_info_name: analyticsFilter,
              tab_info_level: '2',
              tab_info_parent_tab_name: UI_TO_ANALYTICS_NAME_MAP[tab] ?? tab,
            };
            break;
          }

          case TrackingEvents.CtaClick: {
            if (element === undefined) {
              return;
            }
            const analyticsTab = UI_TO_ANALYTICS_NAME_MAP[tab] ?? tab;
            const hasFilter = filter !== undefined;
            const analyticsFilter = hasFilter
              ? (UI_TO_ANALYTICS_NAME_MAP[filter] ?? filter)
              : undefined;
            analyticsData = {
              ...analyticsData,
              interaction_name: 'cta_clicked',
              element_name: element,
              tab_info_name: analyticsFilter ?? analyticsTab,
              tab_info_level: hasFilter ? '2' : '1',
              tab_info_parent_tab_name: hasFilter ? analyticsTab : undefined,
            };
            break;
          }

          default:
            return;
        }

        const finalAnalyticsData = Object.fromEntries(
          Object.entries(analyticsData).filter(([, value]) => value !== undefined),
        );
        window.parent.postMessage({ type: 'kspEvent', data: finalAnalyticsData }, '*');
      }
    },
    [config],
  );
}
